/**
 * Low-level mutation primitives. These are the building blocks bespoke flows
 * compose with — they replace ad-hoc useRef<Map> race handling, manual
 * snapshot/rollback bookkeeping, and inline setQueryData shuffles.
 *
 * Each primitive is independently testable with a mock QueryClient.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import type { ApiError } from '@/api/apiUtils';

type Prefix = readonly unknown[];

/**
 * Snapshot-patch-rollback primitive for optimistic updates on a known cache key.
 *
 * The caller takes a snapshot before patching, then either commits (no-op) or
 * rolls back to the snapshot on error. This is the missing piece useUpdateScene
 * was reimplementing manually.
 */
export function useOptimisticPatch<T>(keyFn: (id: number) => Prefix) {
    const queryClient = useQueryClient();

    return {
        snapshot(id: number): T | undefined {
            return queryClient.getQueryData<T>(keyFn(id));
        },
        async cancelInFlight(id: number) {
            await queryClient.cancelQueries({ queryKey: keyFn(id) });
        },
        patch(id: number, updater: (old: T | undefined) => T | undefined) {
            queryClient.setQueryData<T>(keyFn(id), updater);
        },
        rollback(id: number, prev: T | undefined) {
            if (prev !== undefined) {
                queryClient.setQueryData<T>(keyFn(id), prev);
            }
        },
    };
}

/**
 * Snapshot-patch-rollback over EVERY cached query under a key prefix.
 *
 * The list variant of useOptimisticPatch: consumers may cache the same list
 * under several concrete keys (different filter/version params), and an
 * optimistic insert/remove must hit all of them to be visible everywhere.
 */
export function useOptimisticPrefixPatch<T>(prefix: Prefix) {
    const queryClient = useQueryClient();

    return {
        async cancelInFlight() {
            await queryClient.cancelQueries({ queryKey: prefix });
        },
        snapshot(): Array<[readonly unknown[], T | undefined]> {
            return queryClient.getQueriesData<T>({ queryKey: prefix });
        },
        patch(updater: (old: T | undefined) => T | undefined) {
            queryClient.setQueriesData<T>({ queryKey: prefix }, updater);
        },
        rollback(snapshots: Array<[readonly unknown[], T | undefined]>) {
            for (const [key, data] of snapshots) {
                queryClient.setQueryData(key, data);
            }
        },
    };
}

/**
 * Direct cache write — mutate a query's cached value without triggering a refetch.
 * Useful for syncing computed state into a list cache after a successful mutation.
 */
export function useDirectCacheWrite<T>(key: Prefix) {
    const queryClient = useQueryClient();
    return (updater: (old: T | undefined) => T | undefined) => {
        queryClient.setQueryData<T>(key, updater);
    };
}

interface SafeCreateOrUpdateOpts<TArgs, TResult> {
    /** Stable string identity for race-deduplication (e.g. `scene:${id}:dialogue`). */
    identityKey: (args: TArgs) => string;
    /** Whether the args carry an existing id (skip create entirely). */
    hasId: (args: TArgs) => number | undefined;
    /** Create the resource. Resolves to the new id and the response payload. */
    create: (args: TArgs) => Promise<{ id: number; data: TResult }>;
    /** Update an existing resource by id. */
    update: (args: TArgs, id: number) => Promise<TResult>;
}

/**
 * Race-safe "create if missing, otherwise update" coordinator.
 *
 * Replaces the four-Map-of-refs pattern in useUpdateScene: a created id is
 * cached per identity, in-flight create promises are deduped, and concurrent
 * calls for the same identity await the same create before issuing updates.
 */
export function useSafeCreateOrUpdate<TArgs, TResult>(opts: SafeCreateOrUpdateOpts<TArgs, TResult>): (args: TArgs) => Promise<TResult> {
    const idCache = useRef<Map<string, number>>(new Map());
    const pending = useRef<Map<string, Promise<number>>>(new Map());
    const optsRef = useRef(opts);
    optsRef.current = opts;

    return async (args: TArgs): Promise<TResult> => {
        const { identityKey, hasId, create, update } = optsRef.current;
        const key = identityKey(args);

        const explicitId = hasId(args);
        const cachedId = idCache.current.get(key);
        const knownId = explicitId ?? cachedId;

        if (knownId !== undefined) {
            return update(args, knownId);
        }

        const inFlight = pending.current.get(key);
        if (inFlight) {
            const id = await inFlight;
            return update(args, id);
        }

        const createPromise = (async () => {
            const { id, data } = await create(args);
            idCache.current.set(key, id);
            return { id, data };
        })();

        const idPromise = createPromise.then(r => r.id);
        pending.current.set(key, idPromise);

        try {
            const { data } = await createPromise;
            return data;
        } finally {
            pending.current.delete(key);
        }
    };
}

interface CustomMutationOpts<TParams, TData, TCtx> {
    mutationFn: (params: TParams) => Promise<TData>;
    onMutate?: (params: TParams) => Promise<TCtx> | TCtx;
    onError?: (error: ApiError, params: TParams, ctx: TCtx | undefined) => void;
    onSuccess?: (data: TData, params: TParams, ctx: TCtx | undefined) => void;
    onSettled?: (data: TData | undefined, error: ApiError | null, params: TParams, ctx: TCtx | undefined) => void;
    /** Empty array = explicit skip. Non-empty = invalidate each prefix on settle. */
    invalidate?: readonly Prefix[];
    /** Forwarded to TanStack `useMutation` meta field. */
    meta?: Record<string, unknown>;
}

/**
 * Escape-hatch mutation hook for flows that don't fit the registry. Provides
 * the standard onMutate/onError/onSuccess shape with explicit invalidation —
 * no implicit cache touching — so the behavior is auditable.
 */
export function useCustomMutation<TParams, TData = unknown, TCtx = unknown>(
    opts: CustomMutationOpts<TParams, TData, TCtx>,
): UseMutationResult<TData, ApiError, TParams, TCtx> {
    const queryClient = useQueryClient();
    const { mutationFn, onMutate, onError, onSuccess, onSettled, invalidate, meta } = opts;

    return useMutation<TData, ApiError, TParams, TCtx>({
        mutationFn,
        meta,
        onMutate,
        onError: (error, params, ctx) => onError?.(error, params, ctx),
        onSuccess: (data, params, ctx) => onSuccess?.(data, params, ctx),
        onSettled: (data, error, params, ctx) => {
            if (invalidate && invalidate.length > 0) {
                for (const key of invalidate) {
                    queryClient.invalidateQueries({ queryKey: key as unknown[] });
                }
            }
            onSettled?.(data, error, params, ctx);
        },
    });
}
